import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Skeleton, Progress, Tooltip } from "antd";
import ReactHighcharts from "react-highcharts";
import { healthChallenges } from "../../assets/images";
import { formatAmount } from "../../constants/overview";
import { RoundedSelect } from "../tailwind/atoms";

const defaultColors = [
  "#2168f3", "#48bb78", "#f6ad55", "#9f7aea", "#ed64a6", "#f56565", "#4299e1",
  "#38b2ac", "#ecc94b", "#805ad5"
];

const MAX_VISIBLE_ITEMS = 8;

const CommonSplitPlot = ({ data, loading = false, title = "", shareLabel ='Share', hideImage = false, servicesMap = {}, currentService, handleServiceChange = () => {}, initialMetric = "amount", hideToggle = false, hideSummary = false, hideAov = false, precision = 1, hideBrackets = false }) => {
  const [metric, setMetric] = useState(initialMetric);
  const [donutOptions, setDonutOptions] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const listWrapperRef = useRef(null);
  const [listMaxHeight, setListMaxHeight] = useState("0px");

  const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

  useEffect(() => {
    if (!data) return;
    const categories = Object.keys(data);
    const total = categories.reduce(
      (sum, cat) => sum + Number(data[cat][metric] || 0),
      0
    );

    const chartData = categories.map((cat, idx) => {
      const val = Number(data[cat][metric] || 0);
      return {
        name: cat,
        y: val,
        color: defaultColors[idx % defaultColors.length],
        percentage: total ? ((val / total) * 100).toFixed(precision) : "0.00",
      };
    });

    setDonutOptions({
      chart: { type: "pie", spacingTop: 0 },
      title: { text: "" },
      credits: { enabled: false },
      tooltip: {
        pointFormatter: function () {
          const val =
            metric === "amount"
              ? `₹${numberFormatter.format(this.y)}`
              : numberFormatter.format(this.y);
          const percentStr = ` (${this.options.percentage}%)`
          return `<b>${this.name}:</b> ${val}${percentStr}`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              const val =
                metric === "amount"
                  ? `₹${formatAmount(this.y)}`
                  : numberFormatter.format(this.y);
              if (hideBrackets) return `${this.point.name}: ${this.point.options.percentage}%`;
              return `${this.point.name}: ${val}`;
            },
            style: { fontSize: "11px" },
          },
        },
      },
      series: [{ name: metric, data: chartData }],
    });
  }, [metric, data]);

  // Adjust the wrapper maxHeight when expanded state or data changes
  useEffect(() => {
    const wrapper = listWrapperRef.current;
    if (!wrapper) return;

    // If expanding, set to scrollHeight to show all items
    if (expanded) {
      // allow browser to compute scrollHeight
      const fullHeight = wrapper.scrollHeight;
      setListMaxHeight(`${fullHeight}px`);
      // after animation, clear maxHeight to allow natural height (prevents clipping on resize)
      const t = setTimeout(() => setListMaxHeight("none"), 350);
      return () => clearTimeout(t);
    }

    // If collapsing, set to fixed height for MAX_VISIBLE_ITEMS rows
    // Estimate row height from first child (falls back to 56px)
    const firstChild = wrapper.querySelector(".opd-row");
    const rowHeight = firstChild ? firstChild.getBoundingClientRect().height : 56;
    const collapseHeight = Math.ceil(rowHeight * MAX_VISIBLE_ITEMS);
    // Set to pixel height to animate from current height
    setListMaxHeight(`${collapseHeight}px`);
  }, [expanded, data]);

  if (loading) {
    return (
      <Card style={{ width: "100%" }}>
        <h1 className="text-base text-gray-700 mb-4 leading-none flex gap-4">
          {hideImage ? null : <img src={healthChallenges} className="h-6 w-6" alt="" />}
          <span className="font-fig-semibold">{title}</span>
        </h1>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card title={title} style={{ width: "100%" }}>
        <p>No data available</p>
      </Card>
    );
  }

  const categories = Object.keys(data);
  const total = categories.reduce(
    (sum, cat) => sum + Number(data[cat][metric] || 0),
    0
  );

  const totalAmount = categories.reduce(
    (sum, cat) => sum + Number(data[cat]["amount"] || 0),
    0
  );
  const totalVolume = categories.reduce(
    (sum, cat) => sum + Number(data[cat]["volume"] || 0),
    0
  );

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
          <h1 className="text-base text-gray-700 leading-none flex gap-4">
            {hideImage ? null : <img src={healthChallenges} className="h-6 w-6" alt="" />}
            <span className="font-fig-semibold">{title}</span>
          </h1>

        {!hideToggle && (
          <div className="inline-flex mb-4 bg-gray-100 rounded-md p-1 space-x-1 overflow-x-auto">
            {["volume", "amount"].map((type) => {
              const isActive = metric === type;
              return (
                <button
                  key={type}
                  onClick={() => setMetric(type)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none ${
                    isActive
                      ? "bg-white text-black shadow-sm rounded-md"
                      : "text-gray-500"
                  }`}
                >
                  {type === "volume" ? "Count" : "Value (₹)"}
                </button>
              );
            })}
          </div>
        )}

        {Object.keys(servicesMap).length > 0 && (
          <RoundedSelect
            listClasses="w-34"
            classes="ml-8 -mt-2"
            selectOptions={Object.values(servicesMap)}
            selectValues={Object.keys(servicesMap).map((_, i) => i)}
            curvalue={servicesMap[currentService] || 'Overall'}
            selectHandler={(index) =>
              handleServiceChange('benefit_split', Object.keys(servicesMap)[index])
            }
          />
        )}
      </div>

      <Row gutter={16}>
        <Col span={12}>
          {donutOptions && <ReactHighcharts config={donutOptions} />}
        </Col>

        <Col span={12}>
          {/* Header Row */}
          <div className="flex justify-between items-center mb-2 px-2 sm:px-4">
            <div className="w-1/4" />
            <div className="w-2/4" />
            <div className={`flex justify-end ${hideAov ? 'w-1/12' : 'w-1/4'} gap-6 text-xs sm:text-sm text-gray-500 font-fig-normal tracking-wide`}>
              <span>{shareLabel}</span>
              {!hideAov && <span>AOV</span>}
            </div>
          </div>

          {/* List wrapper with transition */}
          <div
            ref={listWrapperRef}
            style={{
              maxHeight: listMaxHeight === "none" ? "none" : listMaxHeight,
              overflow: "hidden",
              transition: "max-height 320ms ease, opacity 200ms ease",
              opacity: listMaxHeight === "none" ? 1 : 1,
            }}
          >
            {categories.map((cat, idx) => {
              const value = Number(data[cat][metric] || 0);
              const aov =
                data[cat].amount && data[cat].volume
                  ? data[cat].amount / (data[cat].volume || 1)
                  : 0;
              const percent = total ? ((value / total) * 100).toFixed(precision) : "0.00";
              const color = defaultColors[idx % defaultColors.length];

              return (
                <div
                  key={cat}
                  className="opd-row flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 my-1 transition-all hover:scale-[1.01] hover:bg-gray-50 rounded-xl p-2 sm:p-3"
                >
                  {/* Category + Dot */}
                  <div className="flex items-center gap-3 w-full sm:w-1/4">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <Tooltip title={cat}>
                      <span className="font-fig-normal text-gray-800 text-sm truncate">
                        {cat}
                      </span>
                    </Tooltip>
                  </div>

                  {/* Progress Bar */}
                  <Tooltip
                    title={`${cat}: ${metric === "amount" ? `₹${numberFormatter.format(value)}` : numberFormatter.format(value)
                      }${hideBrackets ? ` ${percent}%` : ` (${percent}%)`}`}
                    placement="topLeft"
                  >
                    <div className="flex-1 min-w-0">
                      <Progress
                        percent={Number(percent)}
                        showInfo={false}
                        strokeColor={color}
                        trailColor="#f0f0f0"
                        strokeWidth={8}
                        className="w-full cursor-pointer"
                      />
                    </div>
                  </Tooltip>

                  {/* AOV + Share */}
                  <div className={`flex justify-end gap-6 ${hideAov ? 'w-1/12' : 'w-1/4'} text-sm sm:text-base font-fig-semibold text-gray-800 tabular-nums`}>
                    <span className="text-blue-600 text-right min-w-[3.5rem]">{percent}%</span>
                    {!hideAov && (
                      <span className="text-green-600 text-right min-w-[5rem]">
                        ₹{numberFormatter.format(aov)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand / Collapse Button */}
          {categories.length > MAX_VISIBLE_ITEMS && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setExpanded((s) => !s)}
                className="px-5 py-2 text-sm text-blue-600 font-fig-semibold rounded-full hover:bg-blue-50 transition-all duration-200 flex items-center gap-2 shadow-sm border border-blue-100"
                aria-expanded={expanded}
              >
                <span>{expanded ? "Show Less" : `Show More (${categories.length - MAX_VISIBLE_ITEMS})`}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transform transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}

          {/* Totals Summary */}
          {!hideSummary && (
            <div className="mt-4 shadow-sm rounded-2xl p-4 border border-gray-100">
              <div className="flex items-center justify-between text-sm sm:text-base">
                <div className="flex flex-col">
                  <span className="text-gray-500 font-fig-normal">Total Claims</span>
                  <span className="text-lg font-fig-semibold text-blue-600 mt-1">
                    {numberFormatter.format(totalVolume)}
                  </span>
                </div>

                <div className="h-10 w-px bg-gray-200 mx-4 sm:mx-6" />

                <div className="flex flex-col text-right">
                  <span className="text-gray-500 font-fig-normal">Total Value</span>
                  <span className="text-lg font-fig-semibold text-green-600 mt-1">
                    ₹{numberFormatter.format(totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default CommonSplitPlot;
